import { useEffect, useRef, useState } from 'react'
import { ReactComponent as IconBack } from "assets/images/icons/project-icons/arrow_back.svg"
import { ReactComponent as IconTrash } from "assets/images/icons/project-icons/trash-2.svg"
import FormDrawer from 'components/__new/common/form-drawer'
import CustomRadioButton from 'components/__new/common/CustomRadioButton'
import CurrencyFormat from 'utils/currencyFormat'
import Maybe from 'components/__new/common/Maybe'
import { toast } from 'react-toastify'
import useWarningMessage from 'hooks/useWarningMessage'


const MultipleAirtimePurchaseAmountSetup = ({
    isOpen,
    togglePurchaseModal,
    previewItems,
    setPreviewItems,
    initiatePurchase,
    toggleAmountSetup,
    defaultParams,
    generateClientReference,
}) => {
    
    const [inputsDisabled, setInputsDisabled] = useState(false);
    const [totalFees, setTotalFees] = useState(0);
    const [subTotal, setSubTotal] = useState(0);
    const { warningShown, WarningMessage } = useWarningMessage();
    
    const amountRef = useRef(null);
    
    const [form, setForm] = useState({
        amountType: "same",
        narration: "",
        amount: "",
    });
    
    const removePreviewItem = (id) => {
        const filtered = previewItems?.filter((item) => item?.phoneNumber !== id);
        setPreviewItems(filtered);
    }
    
    const getTransferFee = (amount) => {
        if (amount >= 10 && amount < 5001) {
            return 12
        } else if (amount > 5000 && amount < 50001) {
            return 30
        } else if (amount > 50000) {
            return 60
        }
        return 0
    }
    
    const amountProps = [
        {
            title: "Set Same Amount",
            value: "same",
        },
        {
            title: "Set Different Amount",
            value: "different",
        },
        {
            title: "Split an amount equally",
            value: "split"
        }
    ]
    
    const updateAmount = (phoneNumber, amount) => {
        const updateEntries = previewItems?.map((item) => item.phoneNumber === phoneNumber ? {...item, amount, fee: getTransferFee(amount)} : item)
        setPreviewItems(updateEntries)
    }
    
    const returnToPrevious = () => {
        toggleAmountSetup();
        togglePurchaseModal();
    }
    
    const onProceed = () => {
        const invalidMinAmount = previewItems?.find((item) => item?.amount < 50)
        
        if(invalidMinAmount) {
            toast.error("Please enter a minimum amount of ₦50")
        } else {
            const params = {
                ...defaultParams,
                beneficiaries: previewItems.map((e) => {
                    return {
                        service: e.network?.value,
                        clientReference: generateClientReference(e.phoneNumber),
                        phone: e.phoneNumber,
                        amount: parseInt(e.amount) * 100,
                    }
                }),
            }
            
            toggleAmountSetup();
            initiatePurchase(params);
        }
    }
    
    useEffect(() => {
        if(form.amountType === 'same') {
            setTotalFees(getTransferFee(form.amount) * previewItems?.length)
            setSubTotal(form?.amount * previewItems?.length)
        } else if(form.amountType === 'split') {
            setTotalFees(getTransferFee(form.amount) * previewItems?.length)
            setSubTotal(form?.amount)
            setPreviewItems([...previewItems?.map((item) => ({...item, amount: (form.amount / previewItems?.length), fee: 0}))])
        } 
        // eslint-disable-next-line
    }, [form.amountType, form.amount])
    
    useEffect(() => {
        if(form?.amountType === 'different') {
            setTotalFees(previewItems?.reduce((acc, cur) => acc + cur.fee, 0))
            setSubTotal(previewItems?.reduce((acc, cur) => acc + Number(cur.amount), 0))
        }
        // eslint-disable-next-line
    }, [previewItems])
    
    useEffect(() => {
        if(form.amountType === "same") {
            setInputsDisabled(true);
            setPreviewItems([...previewItems?.map((item) => ({...item, amount: form.amount, fee: 0}))])
        } else if (form.amountType === "different") {
            setInputsDisabled(false);
            setForm({...form, amount: 0})
            setPreviewItems([...previewItems?.map((item) => ({...item, amount: 0, fee: 0}))])
        } else if (form.amountType === "split") {
            setInputsDisabled(true);
        }
        // eslint-disable-next-line
    }, [form.amountType, form.amount])
    
    useEffect(() => {
        setTotalFees(0)
        setSubTotal(0)
        setForm({
            amountType: "same",
            narration: "",
            amount: "0",
        });
        setPreviewItems([...previewItems?.map((item) => ({...item, amount: 0, fee: 0}))]);
        // eslint-disable-next-line
    }, [])
    
    
    return (
        <FormDrawer display="center" size="4xl" isOpen={isOpen} setIsOpen={() => {}}>
            <div className='p-7'>
                <div className='flex items-center'>
                    <div className="w-7 h-7 flex items-center justify-center mr-3 bg-[#f4f4f4] rounded-md">
                        <IconBack
                            className='cursor-pointer'
                            onClick={returnToPrevious}
                        />
                    </div>
                    <div className="font-semibold lg:text-lg">Set Amount</div>
                </div>
                
                <div className="w-full flex items-center justify-items-center lg:items-start lg:justify-between flex-wrap lg:flex-nowrap mt-7">
                    <div className='w-full lg:w-3/5'>
                        <div className='mb-3.5 font-medium'>Set amount to purchase</div>
                        {amountProps.map((item, index) => (
                            <div key={index} onClick={() => setForm({...form, amountType: item.value})} className="mb-2.5 w-max flex items-center cursor-pointer">
                                <CustomRadioButton
                                    widthHeight={5}
                                    checked={form.amountType === item.value}
                                />
                                <div className="ml-2 text-ep-primary-light-200">{item.title}</div>
                            </div>
                        ))}
                        
                        <Maybe condition={form.amountType === "same" || form?.amountType === "split"}>
                            <div className='mt-6'>
                                <label className='text-sm'>Amount (Naira)</label>
                                <input
                                    ref={amountRef}
                                    type="number"
                                    id="prl-input"
                                    placeholder="0.00"
                                    value={form.amount}
                                    className="form-input !h-8 !rounded-lg"
                                    onChange={(e) => setForm({...form, amount: e.target.value})}
                                />
                            </div>
                        </Maybe>
                    </div>
                    
                    <div className='w-full lg:w-2/5 mt-5 lg:mt-0'>
                        <p className="text-sm mb-1 text-[#121619]">Narration</p>
                        <textarea 
                            rows="4"
                            style={{resize: "none"}}
                            placeholder='Add Transaction Notes here...'
                            onChange={(e) => setForm({...form, narration: e.target.value})}
                            className="form-input-textarea !rounded-lg placeholder-[#9ca3af]"
                        />
                    </div>
                </div>
                
                <div className="w-full border-t mt-7 pt-4">
                    <div className="w-full pt-3">
                        {previewItems?.map((item, index) => {
                            return (
                                <div className="mb-7 lg:mb-3 flex items-center justify-center lg:items-center lg:justify-start flex-wrap lg:flex-nowrap" key={index}>
                                    <div className="w-full mb-4 lg:mb-0 lg:w-4/6 flex items-center border border-gray-200 mr-8 px-3 py-2.5 rounded-lg">
                                        <div className='flex items-center justify-center h-8 w-8 rounded-full mr-4'>
                                            <img src={item?.network?.image} alt="biller-icon" />
                                        </div>
                                            
                                        <div className="w-4/5 flex items-center">
                                            <div className='mr-4'>
                                                <p className="text-ep-gray-600">{item?.phoneNumber}</p>
                                                <p className="text-ep-gray-500 uppercase text-sm">{item?.network?.label}</p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className='flex items-end !justify-start w-full lg:w-2/6'>
                                        <div>
                                            <label className='text-sm'>Amount (Naira)</label>
                                            <input
                                                type="number"
                                                id="prl-input"
                                                placeholder="0.00"
                                                value={form.amountType === 'split' ? (form?.amount / previewItems?.length) : form?.amountType === 'same' ? form.amount : item.amount}
                                                disabled={inputsDisabled}
                                                className="form-input !h-8 !rounded-lg"
                                                onChange={(e) => updateAmount(item?.phoneNumber, e.target.value)}
                                            />
                                        </div>
                                    
                                        <button className='ml-3 lg:pb-px' onClick={() => removePreviewItem(item?.phoneNumber)}>
                                            <IconTrash />
                                        </button>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
                
                <div className="rounded-xl py-1.5 px-3 w-full bg-ep-primary-light-100">
                    <div className="flex items-center justify-between w-full">
                        <p className="text-ep-gray-500 text-sm">Recipients</p>
                        <p className="text-ep-gray-600">{previewItems?.length}</p>
                    </div>
                    <div className="flex items-center justify-between w-full">
                        <p className="text-ep-gray-500 text-sm">Fees</p>
                        <p className="text-ep-gray-600">{totalFees}</p>
                    </div>
                    <div className="flex items-center justify-between w-full mt-1 border-t border-dashed pt-1">
                        <p className="text-ep-gray-600">Subtotal</p>
                        <p className="text-ep-gray-600">{CurrencyFormat(subTotal || 0)}</p>
                    </div>
                </div>
                
                <WarningMessage
                    data={previewItems}
                    dependencies={[previewItems, form.amount]}
                />
                
                <div className="w-full flex items-center justify-end pt-8">
                    <button type="button" onClick={returnToPrevious} className="btn btn-transparent-primary mr-3">
                        Cancel
                    </button>
                    
                    <button disabled={warningShown} onClick={onProceed} type="button" className="btn btn-ep-primary">
                        Continue
                    </button>
                </div>
            </div>
        </FormDrawer>
        
    )
}


export default MultipleAirtimePurchaseAmountSetup